
export interface VerificationResult {
  isEligible: boolean;
  matches: HistoricalRecord[];
  matchType: 'EXACT' | 'NAME_ONLY' | 'NONE' | 'WATCHLIST';
}

import { HistoricalRecord, Registrant, WatchlistItem } from '../types';

export interface EligibilityOptions {
  maxYear?: number;
}

interface HistoricalParseOptions {
  maxYear?: number;
}

const parseCSVLine = (line: string): string[] => {
  const columns: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const next = line[i + 1];

    if (char === '"' && inQuotes && next === '"') {
      current += '"';
      i++;
      continue;
    }

    if (char === '"') {
      inQuotes = !inQuotes;
      continue;
    }

    if (char === ',' && !inQuotes) {
      columns.push(current.trim());
      current = '';
      continue;
    }

    current += char;
  }

  columns.push(current.trim());
  return columns;
};

const normalizeHeader = (value: string): string =>
  value
    .replace(/\s+/g, '')
    .toLowerCase()
    .replace(/[（]/g, '(')
    .replace(/[）]/g, ')')
    .replace(/[Ａ-Ｚａ-ｚ]/g, (m) => String.fromCharCode(m.charCodeAt(0) - 0xFEE0));

const findHeaderIndex = (headers: string[] | null, candidates: string[]): number => {
  if (!headers) return -1;
  return headers.findIndex(header => candidates.some(candidate => header.includes(candidate)));
};

const getHistoricalYearSearchText = (cols: string[], headers: string[] | null): string => {
  if (!headers) {
    return cols.slice(2).join(' ');
  }

  const yearSourceIndexes = headers
    .map((header, index) => ({ header, index }))
    .filter(({ header }) =>
      header.includes('備註') ||
      header.includes('remark') ||
      header.includes('note') ||
      header.includes('日期') ||
      header.includes('內容')
    )
    .map(({ index }) => index);

  if (yearSourceIndexes.length === 0) {
    return cols.slice(2).join(' ');
  }

  return yearSourceIndexes.map(index => cols[index] || '').join(' ');
};

const extractHistoricalYear = (text: string, maxYear?: number): number => {
  const gregorianMatch = text.match(/20\d{2}/);
  if (gregorianMatch) {
    return normalizeHistoricalYear(parseInt(gregorianMatch[0]), maxYear);
  }

  const rocPatterns = [
    /(?:民國)?(8\d|9\d|1[0-4]\d)\s*年/,
    /(?:^|[^\d])(8\d|9\d|1[0-4]\d)(?=\d{2}\s*(?:聚會|參加|春季|秋季|人力|資料|醫宣|資源))/,
    /(?:^|[^\d])(8\d|9\d|1[0-4]\d)(?:[./-]\d{1,2})?(?=\s*(?:聚會|參加|春季|秋季|人力|資料|醫宣|資源|$))/
  ];

  for (const pattern of rocPatterns) {
    const match = text.match(pattern);
    if (match) {
      return normalizeHistoricalYear(parseInt(match[1]) + 1911, maxYear);
    }
  }

  return 0;
};

/**
 * Normalizes strings for comparison (trims spaces, lowercases, handles fullwidth/halfwidth, variant characters).
 *
 * 處理以下問題：
 * 1. 空白字符（空格、tab、換行等）
 * 2. 大小寫
 * 3. 全形/半形字符（括號、數字、字母等）
 * 4. 台灣常見異體字（台/臺、峰/峯等）
 * 5. 特殊符號（中間點、連字符等）
 * 6. 教會名稱統一（移除「教會」後綴）
 */
export const normalize = (str: string): string => {
  return str
    .replace(/\s+/g, '')           // 移除所有空白
    .toLowerCase()                  // 轉小寫
    // 全形/半形字符統一為半形
    .replace(/[（]/g, '(')         // 全形左括號 → 半形
    .replace(/[）]/g, ')')         // 全形右括號 → 半形
    .replace(/[，]/g, ',')         // 全形逗號 → 半形
    .replace(/[！]/g, '!')         // 全形驚嘆號 → 半形
    .replace(/[？]/g, '?')         // 全形問號 → 半形
    .replace(/[：]/g, ':')         // 全形冒號 → 半形
    .replace(/[；]/g, ';')         // 全形分號 → 半形
    .replace(/[０-９]/g, (m) => String.fromCharCode(m.charCodeAt(0) - 0xFEE0)) // 全形數字 → 半形
    .replace(/[Ａ-Ｚａ-ｚ]/g, (m) => String.fromCharCode(m.charCodeAt(0) - 0xFEE0)) // 全形字母 → 半形
    // 台灣常見異體字統一
    .replace(/臺/g, '台')          // 臺 → 台
    .replace(/檯/g, '台')          // 檯 → 台（檯北 → 台北，雖然少見但可能出現）
    .replace(/峯/g, '峰')          // 峯 → 峰
    .replace(/萬/g, '万')          // 萬 → 万（簡體，但有時會混用）
    .replace(/裏/g, '里')          // 裏 → 里
    .replace(/眞/g, '真')          // 眞 → 真
    .replace(/羣/g, '群')          // 羣 → 群
    .replace(/敎/g, '教')          // 敎 → 教（異體字）
    // 移除特殊符號（可能在姓名或教會名稱中出現）
    .replace(/[·・･‧]/g, '')      // 移除中間點（各種樣式）
    .replace(/[-－‐‑‒–—―]/g, '')  // 移除連字符（各種樣式）
    .replace(/['']/g, '')          // 移除單引號（彎引號）
    .replace(/[""]/g, '')          // 移除雙引號（彎引號）
    // 教會名稱統一處理
    .replace(/祈禱所$/g, '')       // 祈禱所／教會同一層級簡稱
    .replace(/教會$/g, '')         // 移除結尾的「教會」二字（處理簡稱問題）
    .replace(/真耶穌教会/g, '真耶稣') // 簡體 → 簡體無教會後綴（統一處理）
    .replace(/真耶穌/g, '');       // 移除「真耶穌」前綴（因為都是真耶穌教會）
};

const TITLE_SUFFIXES = ['弟兄', '姐妹', '姊妹', '傳道', '執事', '長老', '牧師', '老師'] as const;

const stripHonorificSuffix = (value: string): string => {
  let current = value.trim();
  for (const suffix of TITLE_SUFFIXES) {
    if (current.endsWith(suffix) && current.length > suffix.length) {
      current = current.slice(0, -suffix.length).trim();
    }
  }
  return current;
};

const expandNameParts = (name: string): string[] => {
  const seeds = [name, ...name.split(/[&＆、，,／/]/g)];
  const parts: string[] = [];

  seeds.forEach(seed => {
    const trimmed = seed.trim();
    if (!trimmed) return;

    parts.push(trimmed);

    const withoutParens = trimmed.replace(/[（(][^）)]*[）)]/g, '').trim();
    if (withoutParens && withoutParens !== trimmed) {
      parts.push(withoutParens);
    }

    for (const match of trimmed.matchAll(/[（(]([^）)]+)[）)]/g)) {
      if (match[1]?.trim()) parts.push(match[1].trim());
    }
  });

  return parts;
};

export const getNameCandidates = (name: string): string[] => {
  const candidates = new Set<string>();

  expandNameParts(name).forEach(part => {
    const trimmed = stripHonorificSuffix(part);
    if (!trimmed) return;

    candidates.add(normalize(trimmed));

    const chineseOnly = (trimmed.match(/[\u3400-\u9fff]+/g) || []).join('');
    if (chineseOnly.length >= 2) {
      candidates.add(normalize(chineseOnly));
    }
  });

  return Array.from(candidates).filter(Boolean);
};

const namesIntersect = (left: string, right: string): boolean => {
  const leftCandidates = getNameCandidates(left);
  const rightCandidates = getNameCandidates(right);
  return leftCandidates.some(candidate => rightCandidates.includes(candidate));
};

const normalizeHistoricalYear = (year: unknown, maxYear?: number): number => {
  const parsed = Number(year);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;

  const rounded = Math.trunc(parsed);
  if (maxYear && rounded > maxYear) return 0;
  return rounded;
};

const normalizeHistoricalMatches = (
  matches: HistoricalRecord[],
  maxYear?: number
): HistoricalRecord[] =>
  matches.map(record => ({
    ...record,
    yearAttended: normalizeHistoricalYear(record.yearAttended, maxYear)
  }));

/**
 * Checks if a registrant exists in the historical database.
 * Returns detailed match information.
 */
export const checkEligibility = (
  registrant: Pick<Registrant, 'name' | 'church' | 'isVerifiedOverride'>,
  historicalDb: HistoricalRecord[],
  watchlist: WatchlistItem[] = [],
  options: EligibilityOptions = {}
): VerificationResult => {
  // Admin manual override check
  if (registrant.isVerifiedOverride) {
    return {
      isEligible: true,
      matches: [],
      matchType: 'EXACT'
    };
  }

  // 動員紀錄：姓名候選交集即可（含括號別名、雙人姓名）；教會不作硬條件
  const regChurch = normalize(registrant.church);

  // Watchlist Check - match both name and church for accuracy
  const isWatchlisted = watchlist.some(item =>
    namesIntersect(item.name, registrant.name) && normalize(item.church) === regChurch
  );

  if (isWatchlisted) {
    return {
      isEligible: true,
      matches: [],
      matchType: 'WATCHLIST'
    };
  }

  const nameMatches = normalizeHistoricalMatches(
    historicalDb.filter(record => namesIntersect(record.name, registrant.name)),
    options.maxYear
  );

  if (nameMatches.length > 0) {
    return {
      isEligible: true,
      matches: nameMatches,
      matchType: 'EXACT'  // Treat name match as EXACT for auto-approval
    };
  }

  return {
    isEligible: false,
    matches: [],
    matchType: 'NONE'
  };
};

/**
 * Helper to parse CSV content.
 * Enhanced to handle messy columns and extract years from "Remarks" or mixed text.
 */
export const parseHistoricalCSV = (
  csvContent: string,
  options: HistoricalParseOptions = {}
): HistoricalRecord[] => {
  const lines = csvContent.split(/\r\n|\r|\n/);
  const data: HistoricalRecord[] = [];
  let headers: string[] | null = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const cols = parseCSVLine(line);

    // Skip header if it looks like a header
    if (cols.some(col => col.includes('姓名')) || cols[0] === 'Name') {
      headers = cols.map(normalizeHeader);
      continue;
    }

    const nameIndex = findHeaderIndex(headers, ['姓名', 'name']);
    const churchIndex = findHeaderIndex(headers, ['教會', 'church']);
    const name = cols[nameIndex >= 0 ? nameIndex : 0] || '';
    const church = cols[churchIndex >= 0 ? churchIndex : 1] || '';

    if (cols.length >= 2 && name) {
      const detectedYear = extractHistoricalYear(
        getHistoricalYearSearchText(cols, headers),
        options.maxYear
      );

      // If we have a name and church, it's a record, even if year is unknown (0)
      data.push({
        id: `imported_${Date.now()}_${i}`,
        name,
        church,
        yearAttended: detectedYear
      });
    }
  }
  return data;
};
